import React, { useEffect, useState } from 'react';
import { useUser } from '../../hooks/useUser';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';
import { Refresh, PlayArrow, CheckCircle, Warning, ToggleOn, ToggleOff, Close } from '@mui/icons-material';
import {
  FormulaVolatility,
  getVolatilities,
  calculateAllVolatilities,
  calculateFormulaVolatility,
  toggleFormulaCalc,
} from '../../services/riskService';

export const VolatilityRisk: React.FC = () => {
  const { data: currentUser } = useUser();
  useCardBar({ visible: false });

  const [formulas, setFormulas] = useState<FormulaVolatility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calculatingAll, setCalculatingAll] = useState(false);
  const [calculatingId, setCalculatingId] = useState<number | null>(null);
  const [lastRunDuration, setLastRunDuration] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getVolatilities();
      setFormulas(data);
    } catch (err: any) {
      console.error('Error fetching volatilities', err);
      setError(err.message || 'Failed to load volatility data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.id) {
      loadData();
    }
  }, [currentUser?.id]);

  const handleCalculateAll = async () => {
    setCalculatingAll(true);
    setError(null);
    try {
      const result = await calculateAllVolatilities();
      setFormulas(result.formulas);
      setLastRunDuration(result.duration);
    } catch (err: any) {
      console.error('Error calculating volatilities', err);
      setError(err.message || 'Failed to calculate volatilities');
    } finally {
      setCalculatingAll(false);
    }
  };

  const handleCalculateOne = async (formulaId: number) => {
    setCalculatingId(formulaId);
    setError(null);
    try {
      const updated = await calculateFormulaVolatility(formulaId);
      setFormulas(prev => prev.map(f => (f.formulaId === formulaId ? updated : f)));
    } catch (err: any) {
      console.error('Error calculating volatility', err);
      setError(err.message || 'Failed to calculate volatility');
    } finally {
      setCalculatingId(null);
    }
  };

  const handleToggleCalc = async (formulaId: number, currentEnabled: boolean) => {
    try {
      await toggleFormulaCalc(formulaId, !currentEnabled);
      setFormulas(prev => prev.map(f =>
        f.formulaId === formulaId ? { ...f, calcEnabled: !currentEnabled } : f
      ));
    } catch (err: any) {
      console.error('Error toggling calc', err);
      setError(err.message || 'Failed to toggle calculation');
    }
  };

  const formatDateTime = (value: string | null) => {
    if (!value) return '—';
    try {
      const d = new Date(value);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return value;
    }
  };

  const formatDateShort = (value: string | null) => {
    if (!value) return '';
    // YYYY-MM-DD → DD/MM/YY
    const [y, m, d] = value.split('-');
    return `${d}/${m}/${y.slice(2)}`;
  };

  const formatPercent = (value: number | null) => {
    if (value === null || value === undefined) return '—';
    return `${(Number(value) * 100).toFixed(1)}%`;
  };

  const formatDuration = (ms: number | null) => {
    if (ms === null || ms === undefined) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const enabledFormulas = formulas.filter(f => f.calcEnabled);
  const calculated = formulas.filter(f => f.sigma !== null);
  const enabledNoData = enabledFormulas.filter(f => f.sigma === null);

  const lastUpdated = formulas
    .filter(f => f.updatedAt)
    .sort((a, b) => new Date(b.updatedAt!).getTime() - new Date(a.updatedAt!).getTime())[0]?.updatedAt;

  // Filter by search, then sort: enabled first, then by code
  const filtered = formulas.filter(f => {
    if (!search) return true;
    const q = search.toLowerCase();
    return f.formulaCode.toLowerCase().includes(q) || f.formulaName.toLowerCase().includes(q);
  });
  const enabledRows = filtered.filter(f => f.calcEnabled).sort((a, b) => a.formulaCode.localeCompare(b.formulaCode));
  const disabledRows = filtered.filter(f => !f.calcEnabled).sort((a, b) => a.formulaCode.localeCompare(b.formulaCode));

  return (
    <div className="px-4 py-3 flex flex-col" style={{ height: 'calc(100vh - 48px)' }}>
      {/* Header */}
      <div className="flex justify-between items-center mb-3 flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-gray-900 text-left">Volatility Risk</h1>
          <p className="text-xs text-gray-500 mt-0.5 text-left">
            Annualized σ and μ calculated from daily spot prices. Toggle formulas on to include in batch calculation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Search formulas..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="px-2.5 py-1 pr-7 text-xs border border-gray-300 rounded-lg w-48 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
              >
                <Close sx={{ fontSize: 14 }} />
              </button>
            )}
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="p-1.5 rounded text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <Refresh className={loading ? 'animate-spin' : ''} sx={{ fontSize: 20 }} />
          </button>
          <button
            onClick={handleCalculateAll}
            disabled={calculatingAll}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center gap-1.5"
          >
            {calculatingAll ? (
              <>
                <Refresh className="animate-spin" sx={{ fontSize: 16 }} />
                Calculating...
              </>
            ) : (
              <>
                <PlayArrow sx={{ fontSize: 16 }} />
                Calculate All
              </>
            )}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-2 flex-shrink-0 px-3 py-1.5 bg-gray-50 rounded border border-gray-200 text-[11px] text-gray-500">
        <span className="font-semibold text-gray-600 mr-4">Legend</span>
        <span className="mr-4"><strong className="text-gray-600">σ</strong> Annualized volatility (std dev of overlapping annual simple returns)</span>
        <span className="mr-4"><strong className="text-gray-600">μ</strong> Mean annual return — <span style={{ color: '#dc2626' }}>red</span> = prices rising, <span style={{ color: '#16a34a' }}>green</span> = falling</span>
        <span className="mr-4"><strong className="text-gray-600">Points</strong> = prices − 252 (lookback window)</span>
        <span><strong className="text-gray-600">VaR</strong> = Exposure × (Z×σ + μ)</span>
      </div>

      {/* Summary strip */}
      <div className="flex items-center gap-5 mb-3 px-3 py-2 bg-gray-50 rounded border border-gray-200 flex-shrink-0 text-xs">
        <span className="text-gray-500">{formulas.length} formulas</span>
        <span className="flex items-center gap-1">
          <CheckCircle sx={{ fontSize: 14 }} className="text-emerald-500" />
          <strong className="text-gray-700">{calculated.length}</strong> calculated
        </span>
        <span className="flex items-center gap-1">
          <ToggleOn sx={{ fontSize: 14 }} className="text-blue-500" />
          <strong className="text-gray-700">{enabledFormulas.length}</strong> enabled
        </span>
        {enabledNoData.length > 0 && (
          <span className="flex items-center gap-1">
            <Warning sx={{ fontSize: 14 }} className="text-amber-500" />
            <strong className="text-gray-700">{enabledNoData.length}</strong> insufficient data
          </span>
        )}
        <span className="ml-auto text-gray-400">
          {lastRunDuration && <>Last run: {lastRunDuration} · </>}
          {lastUpdated && <>Updated: {formatDateTime(lastUpdated)}</>}
        </span>
      </div>

      {error && (
        <div className="mb-3 p-2 rounded bg-red-50 text-red-700 text-xs flex-shrink-0">{error}</div>
      )}

      {/* Scrollable table */}
      {loading ? (
        <div className="flex items-center justify-center h-32 text-gray-500 text-xs">Loading...</div>
      ) : formulas.length === 0 ? (
        <div className="text-gray-500 text-xs">No formulas found.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg flex-1 min-h-0 overflow-auto">
          <table className="w-full table-fixed">
            <colgroup>
              <col style={{ width: 44 }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: 44 }} />
            </colgroup>
            <thead className="sticky top-0 z-10">
              <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                <th className="px-2 py-2 text-center">Calc</th>
                <th className="px-3 py-2">Formula</th>
                <th className="px-3 py-2 text-right">σ</th>
                <th className="px-3 py-2 text-right">μ</th>
                <th className="px-3 py-2 text-right">Points</th>
                <th className="px-3 py-2">Date Range</th>
                <th className="px-3 py-2">Last Calculated</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...enabledRows, ...(enabledRows.length > 0 && disabledRows.length > 0 ? [null] : []), ...disabledRows].map((f, idx) => {
                if (f === null) {
                  return (
                    <tr key="__sep__">
                      <td colSpan={8} className="px-3 py-1 bg-gray-100">
                        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Not enabled</span>
                      </td>
                    </tr>
                  );
                }
                const isCalculating = calculatingId === f.formulaId;
                const hasSigma = f.sigma !== null;
                const muNum = f.mu !== null ? Number(f.mu) : null;
                return (
                  <tr
                    key={f.formulaId}
                    className={`hover:bg-gray-50 transition-colors ${
                      !f.calcEnabled ? 'opacity-40' : !hasSigma ? 'bg-amber-50/30' : ''
                    }`}
                  >
                    {/* Calc toggle */}
                    <td className="px-2 py-1.5 text-center">
                      <button
                        onClick={() => handleToggleCalc(f.formulaId, f.calcEnabled)}
                        className="p-0 border-0 bg-transparent cursor-pointer"
                        title={f.calcEnabled ? 'Disable calculation' : 'Enable calculation'}
                      >
                        {f.calcEnabled ? (
                          <ToggleOn sx={{ fontSize: 28, color: '#3b82f6' }} />
                        ) : (
                          <ToggleOff sx={{ fontSize: 28, color: '#d1d5db' }} />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-1.5">
                      <span className={`text-xs font-semibold ${!f.calcEnabled ? 'text-gray-400' : 'text-gray-900'}`}>
                        {f.formulaCode}
                      </span>
                      <span className="block text-[10px] text-gray-400 truncate">{f.formulaName}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <span className={`font-mono text-xs ${hasSigma ? 'font-semibold text-gray-900' : 'text-gray-300'}`}>
                        {formatPercent(f.sigma)}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <span
                        className="font-mono text-xs font-semibold"
                        style={{ color: muNum !== null ? (muNum > 0 ? '#dc2626' : '#16a34a') : '#d1d5db' }}
                      >
                        {formatPercent(f.mu)}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <span className="font-mono text-xs text-gray-600" title={f.priceCount > 0 ? `${f.priceCount.toLocaleString()} spot prices → ${f.dataPoints?.toLocaleString()} overlapping returns` : ''}>
                        {f.dataPoints?.toLocaleString() ?? '—'}
                      </span>
                    </td>
                    <td className="px-3 py-1.5">
                      {f.dateFrom && f.dateTo ? (
                        <span className="text-xs text-gray-600">
                          {formatDateShort(f.dateFrom)} → {formatDateShort(f.dateTo)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-gray-500">
                      {formatDateTime(f.updatedAt)}
                      {f.calcDurationMs !== null && (
                        <span className="ml-2 inline-flex px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-600 rounded-full border border-blue-200">{formatDuration(f.calcDurationMs)}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button
                        onClick={() => handleCalculateOne(f.formulaId)}
                        disabled={isCalculating || calculatingAll}
                        className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-30"
                        title={`Recalculate ${f.formulaCode}`}
                      >
                        {isCalculating ? (
                          <Refresh className="animate-spin" sx={{ fontSize: 16 }} />
                        ) : (
                          <PlayArrow sx={{ fontSize: 16 }} />
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VolatilityRisk;
